// The live edit/view state of one editor session (see liveState.js).
//
// A session asked to edit reads the file before the editor mounts (so a file
// that is already locked opens read only, not edit-then-reload), then every
// POLL_MS and whenever the window regains focus. A 403/404 — on that read or
// on the editor's own token request (reportFailure) — is "no access": the
// session keeps checking, and a good read clears it.

import { useCallback, useEffect, useRef, useState } from 'react';
import baseAxios from '@/shared/utils/request.js';
import { NODE } from '@/shared/utils/urls.js';
import { lockOf } from '@/shared/utils/nodeAccess.js';
import { POLL_MS, editorMode, failureCode, isNoAccess } from './liveState.js';

const sameFailure = (a, b) => Boolean(a) && Boolean(b) && a.code === b.code && a.status === b.status && a.reason === b.reason;

export function useLiveEditState(fileUuid, { requested = 'edit', enabled = true } = {}) {
  const [node, setNode] = useState(null);
  const [settled, setSettled] = useState(false); // the first read answered (either way)
  const [failure, setFailure] = useState(null); // {code, status}: no access (403/404)
  const [fault, setFault] = useState(null); // {code: 'error', status}: the first read failed otherwise
  const inflight = useRef(false);
  const generation = useRef(0);
  const settledRef = useRef(false);

  const keepFailure = useCallback((next) => {
    setFailure((prev) => (sameFailure(prev, next) ? prev : next));
  }, []);

  // A new file is a new session.
  useEffect(() => {
    generation.current += 1;
    inflight.current = false;
    settledRef.current = false;
    setNode(null);
    setSettled(false);
    setFailure(null);
    setFault(null);
  }, [fileUuid]);

  const refresh = useCallback(async () => {
    if (!enabled || !fileUuid || inflight.current) return;
    inflight.current = true;
    const mine = generation.current;
    try {
      const res = await baseAxios({ method: 'GET', url: NODE.replace('{node_uuid}', encodeURIComponent(String(fileUuid))) });
      if (mine !== generation.current) return;
      setNode(res?.data || null);
      setFailure(null);
    } catch (error) {
      if (mine !== generation.current) return;
      const status = error?.response?.status ?? null;
      if (status === 403 || status === 404) {
        keepFailure({ code: failureCode(status), status });
      } else if (!settledRef.current) {
        // Offline or a fault on the first read: open as asked and let the
        // editor's own token request decide. Later misses keep the last answer.
        setFault({ code: 'error', status });
      }
    } finally {
      if (mine === generation.current) {
        inflight.current = false;
        settledRef.current = true;
        setSettled(true);
      }
    }
  }, [fileUuid, enabled, keepFailure]);

  /** The editor's token request was refused: 403/404 is "no access" like a refused read. */
  const reportFailure = useCallback((info) => {
    const code = failureCode(info?.status ?? null);
    // `reason`: the server's code (`missing_scope`: the person's access in
    // docs opens no documents — checking again won't change that).
    if (code === 'no_access' || code === 'not_found') {
      keepFailure({ code, status: info.status, ...(info?.code === 'missing_scope' ? { reason: info.code } : {}) });
    }
  }, [keepFailure]);

  // The first read of an edit session.
  useEffect(() => {
    if (enabled && fileUuid && requested === 'edit') refresh();
  }, [enabled, fileUuid, requested, refresh]);

  // Keep reading while editing — and while there is no access, to notice it arriving.
  const watching = enabled && Boolean(fileUuid) && (requested === 'edit' || isNoAccess(failure));
  useEffect(() => {
    if (!watching) return undefined;
    const timer = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [watching, refresh]);

  const { mode, reason } = editorMode(requested, node);
  return {
    ready: requested !== 'edit' || settled,
    node,
    mode,
    reason,
    lock: lockOf(node),
    failure,
    fault,
    refresh,
    reportFailure,
  };
}
